<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<template>
  <div class="flex-search-details-npi">
    <div class="operations-card">
      <div class="left-card">
        <div class="form-group">
          <div class="btn-group">
            <ec-dropdown>
              <v-btn color="white" slot="activator">
                <v-icon color="primary">fa-columns</v-icon>
              </v-btn>

              <div class="dropdown-wrapper app-select-options">
                <h3>Select Columns to Display</h3>

                <v-select-list
                  :items="headerOptions"
                  action
                  :selected-items="selectedHeaderValues"
                  color="primary"
                  @select="updateSelectedHeaderValues"
                />
              </div>
            </ec-dropdown>
          </div>
        </div>
        <div class="form-group">
          <label>Search criteria</label>
          <v-text-field
            class="filter search-filter"
            solo
            v-model="searchCriteriaFilter"
            @input="debouncedSearch(true)"
          />
        </div>
        <div class="form-group">
          <label>Record status</label>
          <v-select
            :items="statusFilterOptions"
            v-model="recordStatusFilter"
            class="filter"
            content-class="app-select-options"
            @input="debouncedSearch(true)"
            multiple
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ recordStatusFilter.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </div>
        <div class="form-group">
          <label>NPI Status</label>
          <v-select
            :items="npiStatusesFilterOptions"
            v-model="npiStatusFilter"
            class="filter"
            content-class="app-select-options"
            @input="debouncedSearch(true)"
            multiple
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ npiStatusFilter.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </div>
        <div class="form-group" v-if="showOptOutStatus">
          <label>Opt Out Status</label>
          <v-select
            :items="optOutStatusesOptions"
            v-model="optOutStatusFilter"
            class="filter"
            content-class="app-select-options"
            @input="debouncedSearch(true)"
            :multiple="false"
            clearable
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
            </template>
          </v-select>
        </div>
        <div class="form-group" v-if="showExclusionStatus">
          <label>Exclusion Status</label>
          <v-select
            :items="exclusionStatusesOptions"
            v-model="exclusionStatusFilter"
            class="filter"
            content-class="app-select-options"
            @input="debouncedSearch(true)"
            :multiple="false"
            clearable
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
            </template>
          </v-select>
        </div>
        <v-btn color="primary" class="reset-btn" @click="resetFilters">
          Reset
        </v-btn>
      </div>

      <status-dropdown-dialog
        class="bulk-status-dropdown"
        :disabled="!selectedItems.length"
        @status-change="
          saveBulkStatusChange($event, API.FLEXBATCH_NPI_DETAILS_BYIDS)
        "
        :status-infos="statusInfos"
        :status-change-loading="statusChangeLoading"
      >
        Bulk Status Change
      </status-dropdown-dialog>
    </div>

    <v-data-table
      ref="npitable"
      class="ec-table npi-table"
      v-model="selectedItems"
      :headers="selectedHeaders"
      :items="items"
      item-key="id"
      select-all
      sort-icon="arrow_drop_up"
      hide-actions
    >
      <template slot="items" slot-scope="props">
        <tr
          @click="updateSlideIn(props.item.id, props.item.status)"
          :key="props.item.id"
        >
          <td @click.stop>
            <v-checkbox
              color="primary"
              v-if="props.item.status != 'CLEARED'"
              v-model="props.selected"
              hide-details
            />
          </td>
          <td>
            <div class="table-cell-wrapper">{{ props.item.npi }}</div>
          </td>
          <td v-show="isHeaderSelected('name')">
            <div class="table-cell-wrapper">{{ props.item.name }}</div>
          </td>
          <td v-show="isHeaderSelected('nppesName')">
            {{ props.item.nppesName }}
          </td>
          <td
            :style="{ color: statusColor(props.item.npiStatus) }"
            v-show="isHeaderSelected('npiStatus')"
          >
            {{ props.item.npiStatus }}
          </td>
          <td
            :style="{ color: statusColor(props.item.optOutStatusText) }"
            v-show="isHeaderSelected('optOutStatusText')"
          >
            {{ props.item.optOutStatusText }}
          </td>
          <td
            :style="{ color: statusColor(props.item.exclusionStatusText) }"
            v-show="isHeaderSelected('exclusionStatusText')"
          >
            {{ props.item.exclusionStatusText }}
          </td>
          <td v-show="isHeaderSelected('status')" class="dropdown-column">
            <status-dropdown-dialog
              v-if="props.item.status !== 'CLEARED'"
              @click.capture.stop.prevent
              :status="props.item.status"
              :status-infos="statusInfos"
              @status-change="
                saveStatusChange(
                  $event,
                  [props.item.id],
                  API.FLEXBATCH_NPI_DETAILS_BYIDS
                )
              "
              :bottom-up-dropdown="bottomUpDropdowns[props.index]"
              :bottom-up-confirmdialog="bottomUpConfirmdialogs[props.index]"
              @show-dropdown="
                updateBottomUp(
                  $event,
                  'bottomUpDropdowns',
                  props.index,
                  tableQuery
                )
              "
              @show-confirmdialog="
                updateBottomUp(
                  $event,
                  'bottomUpConfirmdialogs',
                  props.index,
                  tableQuery
                )
                hasConfirmdialogOpened = $event
              "
              :force-dropdown-closed="hasConfirmdialogOpened"
            />
            <div v-else class="clear-status">Clear</div>
          </td>
          <td v-show="isHeaderSelected('latestNote')">
            {{ props.item.latestNote }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <div class="text-xs-center pt-2">
      <v-pagination v-model="page" :length="pages" :total-visible="7" />
    </div>
  </div>
</template>

<script>
import {
  API,
  createFilterList,
  exclusionStatuses,
  npiStatuses,
  optOutStatuses,
  RECORDS_PER_PAGE,
  SEARCH_DEBOUNCE_TIMEOUT,
  statusColor,
  statusInfos
} from "@/common/constants"
import axios from "axios"
import { debounce } from "lodash"
import EcDropdown from "@/components/Dropdown"
import EventBus from "@/common/event-bus"
import { mapMutations, mapGetters } from "vuex"
import { deleteElFromArry } from "@/service/util"
import StatusDropdownDialog from "@/components/StatusDropdownDialog"
import saveStatusChange from "@/mixins/statusChange"
import flipDialogUp from "@/mixins/flipDialogUp"

const thePromise = Promise.resolve()

const headers = [
  {
    value: "npi",
    width: "140px",
    text: "NPI"
  },
  {
    value: "name",
    width: "200px",
    text: "Name"
  },
  {
    value: "nppesName",
    fixed: false,
    width: "200px",
    text: "Name From NPPES"
  },
  {
    value: "npiStatus",
    fixed: false,
    width: "180px",
    text: "NPI Status"
  },
  {
    value: "optOutStatusText",
    fixed: false,
    width: "180px",
    text: "Opt Out Status"
  },
  {
    value: "exclusionStatusText",
    fixed: false,
    width: "180px",
    text: "Exclusion Result"
  },
  {
    value: "status",
    fixed: false,
    width: "180px",
    text: "Status"
  },
  {
    value: "latestNote",
    fixed: false,
    width: "180px",
    text: "Latest Note"
  }
]

const eventModifier = "NPI"
const componentName = "FlexSearchDetailsNPI"

export default {
  name: componentName,
  displayName: "NPI Results",

  props: {
    batchId: {
      type: String,
      default: "-1"
    }
  },
  data: () => ({
    page: 0,
    pages: 1,

    items: [],
    showName: true,
    showOptOutStatus: true,
    showExclusionStatus: true,

    statusInfos,
    statusColor,
    optOutStatuses,
    exclusionStatuses,
    npiStatuses,
    tableQuery: ".npi-table table.v-datatable",
    API,
    selectedHeaderValues: headers.map(({ value }) => value),
    headerOptions: [
      {
        value: "nppesName",
        fixed: false,
        width: "200px",
        text: "Name From NPPES"
      },
      {
        value: "latestNote",
        fixed: false,
        width: "180px",
        text: "Latest Note"
      }
    ],
    selectedItems: [],
    hasConfirmdialogOpened: false,
    bottomUpDropdowns: [],
    bottomUpConfirmdialogs: [],
    currentSlideInId: -1,

    optOutStatusFilter: null,
    recordStatusFilter: [],
    exclusionStatusFilter: null,
    npiStatusFilter: [],
    searchCriteriaFilter: ""
  }),

  mixins: [saveStatusChange, flipDialogUp],
  methods: {
    ...mapMutations([
      "setSlideIn",
      "setStatusInfos",
      "hasClearNonBtn",
      "setEventModifier",
      "restoreStates"
    ]),

    debouncedSearch: debounce(function(newSearch) {
      thePromise.then(() => this.search(newSearch))
    }, SEARCH_DEBOUNCE_TIMEOUT),
    initPage() {
      this.page = 1
      this.selectedItems = []
    },
    search(newSearch = false) {
      this.loading = true

      if (newSearch) {
        this.initPage()
      }

      const page = this.page
      console.log(
        `Fetching flexible batch search details page: ${page} for batchId: ${this.batchId}, newSearch: ${newSearch}`
      )

      axios
        .get(API.FLEXBATCH_NPI_DETAILS.replace(":batchId", this.batchId), {
          params: {
            limits: RECORDS_PER_PAGE,
            page,
            requireTotal: newSearch,
            optOutStatus: this.optOutStatusFilter,
            recordStatus: this.recordStatusFilter,
            exclusionStatus: this.exclusionStatusFilter,
            npiStatus: this.npiStatusFilter.flatMap(ele => ele),
            searchCriteria: this.searchCriteriaFilter
          }
        })
        .then(response => {
          if (newSearch)
            this.pages = Math.ceil(response.data.total / RECORDS_PER_PAGE)

          this.items = response.data.npiSearches
          this.showName = response.data.nameSearch
          this.showOptOutStatus = response.data.optOutSearch
          this.showExclusionStatus = response.data.exclusionSearch
        })
        .finally(() => (this.loading = false))
    },
    resetFilters() {
      this.optOutStatusFilter = null
      this.recordStatusFilter = []
      this.exclusionStatusFilter = null
      this.npiStatusFilter = []
      this.searchCriteriaFilter = ""
      this.debouncedSearch(true)
    },

    isHeaderSelected(value) {
      if (value === "name") {
        return this.showName
      }
      if (value === "optOutStatusText") {
        return this.showOptOutStatus
      }
      if (value === "exclusionStatusText") {
        return this.showExclusionStatus
      }
      return this.selectedHeaderValues.includes(value)
    },
    updateSelectedHeaderValues({ value }) {
      const arry = this.selectedHeaderValues
      deleteElFromArry(arry, value) || arry.push(value)
    },
    updateSlideIn(id, matchstatus) {
      this.currentSlideInId = id
      axios
        .get(
          API.FLEXBATCH_NPI_DETAILS_BYID.replace(
            ":batchId",
            this.batchId
          ).replace(":id", id)
        )
        .then(response => {
          this.setSlideIn(response.data)
          this.setStatusInfos(statusInfos)
          this.hasClearNonBtn(matchstatus === "CLEARED")
          this.setEventModifier(eventModifier)
        })
    },
    restoreDataStates(dataStates) {
      for (const prop in dataStates) {
        this[prop] = dataStates[prop]
      }
    }
  },

  watch: {
    selectedItems: function(oldList, newList) {
      this.selectedItems.filter(item => {
        if (item.status === "CLEARED") {
          oldList.pop(item)
        }
      })
    },
    page(oldVal, newVal) {
      if (!this.loading) this.search()
    }
  },

  computed: {
    ...mapGetters(["notificationStates"]),
    optOutStatusesOptions() {
      return createFilterList(this.optOutStatuses)
    },
    exclusionStatusesOptions() {
      return createFilterList(this.exclusionStatuses)
    },
    statusFilterOptions() {
      return createFilterList(this.statusInfos)
    },
    npiStatusesFilterOptions() {
      return Object.entries(this.npiStatuses).map(item => ({
        value: item[1].value,
        text: item[1].text
      }))
    },
    selectedHeaders() {
      return headers.filter(({ value }) => this.isHeaderSelected(value))
    }
  },
  beforeDestroy() {
    EventBus.$off("global-status-change" + eventModifier)
  },
  created() {
    if (history.state && history.state[componentName]) {
      this.restoreStates(history.state[componentName].notificationStates)
      this.restoreDataStates(history.state[componentName].dataStates)
      EventBus.$emit("delete-state", componentName)
    }

    this.search(true)
    EventBus.$on("global-status-change" + eventModifier, evt => {
      const id = this.currentSlideInId
      if (id !== -1) {
        this.saveStatusChange(
          evt,
          [id],
          API.FLEXBATCH_NPI_DETAILS_BYIDS
        ).then(() => this.updateSlideIn(id))
      }
    })
    EventBus.$on("to-external", backup => {
      backup({
        [componentName]: {
          notificationStates: this.notificationStates,
          dataStates: {
            currentSlideInId: this.currentSlideInId
          }
        }
      })
    })
  },
  components: { StatusDropdownDialog, EcDropdown }
}
</script>

<style lang="scss" scoped>
.flex-search-details-npi {
  padding: 2.25rem;
  padding-bottom: 1rem;

  .npi-table tr:not(.v-datatable__group-row) {
    &:nth-child(odd) {
      background-color: $color-off-white;
    }
  }

  .ec-dropdown {
    .dropdown-wrapper {
      overflow-y: hidden;
      h3 {
        padding-right: 0.5rem;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/FlexSearchDetails-NPI.vue